import { FigureSettingsDto } from "aic";
import { ComparatorTypeDto } from "components/annotations/interface";
import { IDropdownOption } from "components/common/HighButton";
import { IHierarchyProperty } from "components/hierarchypage/HierarchyProperties";
import { eApplicationPermission } from "functions/api/enums";
import { HelpLink } from "functions/api/helplinks/loadHelpLinks";
import { StyleReadDto } from "functions/api/styles/getAllStyles";
import { Patch } from "rfc6902";
import {
  HierarchyContextProps,
  IHierarchyAction,
  IHierarchyValidationSaveBlockerAction,
} from "../hierarchy/interface";
import { ITlfAction, TlfContextProps } from "../tlf/interface";
import type { IMosaicTocExpanded, ITocAction } from "../toc/interface";
import { ISaveBlockers } from "./saveBlockers";

export const UNDO = "UNDO";
export const REDO = "REDO";

/**
 * Reversible changes to editable data across the unified context.
 */
export interface IUnifiedHistoryEntry {
  forward: Patch;
  reverse: Patch;
}

/**
 * Moves through the unified editable-data history.
 */
export interface IUnifiedHistoryAction {
  operation: typeof UNDO | typeof REDO;
  steps?: number;
  calledFrom?: string;
}

/**
 * Application settings
 */
export interface IAppSettings {
  annotationComparatorTypes: ComparatorTypeDto[];
  appSettingsLoaded: boolean;
  applicationPermissions: eApplicationPermission[];
  backEndUrl: string;
  backEndVersion: string;
  colWidthMod: number;
  crystalUrl: string;
  defaultColumnWidth: number;
  defaultDownloadOptions: IDropdownOption[];
  defaultLevelProperties: { [key: string]: IHierarchyProperty[] };
  defaultTableStyle: React.CSSProperties;
  displayName: string;
  domain: string;
  figureSettings: FigureSettingsDto;
  helpLinks: HelpLink[];
  isAdmin: boolean | null;
  maintenanceMessage: string;
  maintenanceMode: boolean;
  prid: string;
  saveBlockers: ISaveBlockers;
  styles: StyleReadDto[];
  userId: number;
  allowedReportTypes: string[];
}

/**
 * Unified application state combining app-level settings with hierarchy and ToC slices.
 * Extends {@link IAppSettings} so that existing consumers can use this type where
 * {@link IAppSettings} is expected.
 */
export interface IUnifiedState extends IAppSettings {
  /** Chronological reversible patches, never full slice snapshots. */
  undoHistory: IUnifiedHistoryEntry[];
  /** Number of history entries currently applied. */
  undoIndex: number;
  /** Current hierarchy slice managed by the unified reducer */
  hierarchyState: HierarchyContextProps;
  /** Current ToC slice managed by the unified reducer */
  tocState: IMosaicTocExpanded;
  /** Current TLF slice managed by the unified reducer */
  tlfState: TlfContextProps;
}

/**
 * The combined action type for the unified AppSettings context reducer.
 * Encompasses hierarchy and ToC domain actions dispatched from their respective providers.
 */
export type IUnifiedAction =
  | IHierarchyAction
  | IHierarchyValidationSaveBlockerAction
  | ITlfAction
  | ITocAction
  | IUnifiedHistoryAction;

/**
 * The value provided by {@link AppSettingsContext}, following the ARS-style
 * `{ state, dispatch }` pattern.
 */
export interface IAppSettingsContextValue {
  /** Unified application state including app settings, hierarchy, and ToC slices */
  state: IUnifiedState;
  /** Dispatcher for all unified operations across hierarchy and ToC domains */
  dispatch: React.Dispatch<IUnifiedAction>;
}
